import { ProList } from '@ant-design/pro-components';
import { App, Empty, Flex, Popconfirm, Typography } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { queryAgentChatThreads, removeAgentChatThread } from '../services/chat';

const REMOVABLE_ROLES = new Set(['owner', 'admin']);
const formatTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');

const AgentChatThreadList = ({
  currentRole,
  onThreadRemove = () => {},
  onThreadSelect,
  organizationId,
  refreshKey,
}) => {
  const { message } = App.useApp();
  const [removalKey, setRemovalKey] = useState(0);
  const [removingThreadId, setRemovingThreadId] = useState();
  const canRemove = REMOVABLE_ROLES.has(currentRole);
  const handleRemove = async (item) => {
    if (!organizationId || !item?.id) {
      return;
    }
    setRemovingThreadId(item.id);
    try {
      await removeAgentChatThread({ organizationId, threadId: item.id });
      message.success('创作记录已删除');
      onThreadRemove(item);
      setRemovalKey((value) => value + 1);
    } catch {
      message.error('创作记录删除失败，请重试');
    } finally {
      setRemovingThreadId(undefined);
    }
  };

  return (
    <ProList
      dateFormatter={false}
      locale={{ emptyText: <Empty description="暂无创作记录" /> }}
      columns={[
        {
          dataIndex: 'title',
          fieldProps: {
            allowClear: true,
            placeholder: '搜索创作标题',
          },
          listSlot: 'title',
          render: (_, item) => (
            <Typography.Link
              href="#"
              strong
              ellipsis
              onClick={(event) => {
                event.preventDefault();
                onThreadSelect(item);
              }}
            >
              {item.title || '新的创作对话'}
            </Typography.Link>
          ),
        },
        {
          dataIndex: 'createdAtRange',
          fieldProps: {
            allowClear: true,
            placeholder: '创作时间',
          },
          hideInTable: true,
          search: {
            transform: (value) => ({
              end: value?.[1] ? dayjs(value[1]).endOf('day').valueOf() : undefined,
              start: value?.[0] ? dayjs(value[0]).startOf('day').valueOf() : undefined,
            }),
          },
          valueType: 'dateRange',
        },
        {
          dataIndex: 'preview',
          listSlot: 'description',
          search: false,
          render: (_, item) => (
            <Flex align="center" justify="space-between" gap="small">
              <Typography.Text type="secondary" ellipsis>
                {item.preview || '暂无对话摘要'}
              </Typography.Text>
              <Typography.Text type="secondary" styles={{ root: { textWrapMode: 'nowrap' } }}>
                {formatTime(item.createdAt || item.updatedAt)}
              </Typography.Text>
              {canRemove ? (
                <Popconfirm
                  cancelText="取消"
                  okButtonProps={{ danger: true, loading: removingThreadId === item.id }}
                  okText="删除"
                  title="确定删除这条创作记录吗？"
                  description="关联创作产出也会一起删除，删除后不可恢复。"
                  onConfirm={() => handleRemove(item)}
                >
                  <Typography.Link
                    href="#"
                    type="danger"
                    styles={{ root: { textWrapMode: 'nowrap' } }}
                    onClick={(event) => {
                      event.preventDefault();
                    }}
                  >
                    删除
                  </Typography.Link>
                </Popconfirm>
              ) : null}
            </Flex>
          ),
        },
      ]}
      params={{ organizationId, refreshKey, removalKey }}
      pagination={{ showSizeChanger: true }}
      request={async (params) => {
        if (!organizationId) {
          return { data: [], success: true, total: 0 };
        }
        const response = await queryAgentChatThreads({
          current: params.current,
          end: params.end,
          organizationId,
          pageSize: params.pageSize,
          start: params.start,
          title: typeof params.title === 'string' ? params.title.trim() || undefined : undefined,
        });
        return {
          data: response.data || [],
          success: true,
          total: Number(response.meta?.total || 0),
        };
      }}
      rowKey="id"
      search={{
        labelWidth: 0,
        span: 12,
        style: { padding: 0 },
      }}
      split
      size="small"
    />
  );
};

AgentChatThreadList.propTypes = {
  currentRole: PropTypes.string,
  onThreadRemove: PropTypes.func,
  onThreadSelect: PropTypes.func,
  organizationId: PropTypes.string,
  refreshKey: PropTypes.number,
};

AgentChatThreadList.defaultProps = {
  currentRole: undefined,
  onThreadSelect: () => {},
  organizationId: undefined,
  refreshKey: 0,
};

export default AgentChatThreadList;
